import React from 'react';
import styled, { css } from 'styled-components/macro';
import { fontSizePalette, fontWeightPalette } from 'utils/styleConstants';

const fontWeights = {
  heavy: 800,
  medium: 500,
  roman: 400,
  light: 300,
};

const textStyles = {
  h1: {
    fontSize: fontSizePalette.xxLarge,
    lineHeight: '100%',
    letterSpacing: '0',
    fontWeight: fontWeightPalette.medium,
  },
  h2: {
    fontSize: fontSizePalette.xlLarge,
    lineHeight: '100%',
    letterSpacing: '-0.88px',
    fontWeight: fontWeightPalette.bold,
  },
  h3: {
    fontSize: fontSizePalette.xLarge,
    lineHeight: '100%',
    letterSpacing: '-0.88px',
    fontWeight: fontWeightPalette.bold,
  },
  h4: {
    fontSize: fontSizePalette.large,
    lineHeight: '100%',
    letterSpacing: '-0.5px',
    fontWeight: fontWeightPalette.bold,
  },
  h5: {
    fontSize: fontSizePalette.medium,
    lineHeight: '100%',
    letterSpacing: '-0.5px',
    fontWeight: fontWeightPalette.bold,
  },
  body: {
    fontSize: fontSizePalette.small,
    lineHeight: '100%',
    letterSpacing: '-0.5px',
    fontWeight: fontWeightPalette.medium,
  },
  caption: {
    fontSize: fontSizePalette.xSmall,
    lineHeight: '100%',
    letterSpacing: '1%',
    fontWeight: fontWeightPalette.bold,
  },
};

const StyledText = styled.span<{ variant?: keyof typeof textStyles; underline?: boolean }>`
  font-family: ${({ theme }) => theme.fontFamily};

  ${(props) =>
    props.variant &&
    css`
      font-size: ${textStyles[props.variant].fontSize};
      line-height: ${textStyles[props.variant].lineHeight};
      letter-spacing: ${textStyles[props.variant].letterSpacing};
      font-weight: ${textStyles[props.variant].fontWeight};
      leading-trim: 'Cap height';
      color: ${props.theme.colorPalette.grey.grey10};
    `}

  ${({ underline }) =>
    underline &&
    css`
      text-decoration: underline;
    `}
`;

type TypographyProps = {
  variant?: keyof typeof textStyles;
  children: React.ReactNode;
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  weight?: keyof typeof fontWeights | number;
  underline?: boolean;
};

const Typography: React.FC<TypographyProps> = ({
  variant = 'body',
  children,
  as = 'span',
  weight,
  underline = false,
}) => {
  const Component = StyledText.withComponent(as);
  const customStyle = weight ? { fontWeight: fontWeights[weight as keyof typeof fontWeights] || weight } : {};

  return (
    <Component variant={variant} underline={underline} style={customStyle}>
      {children}
    </Component>
  );
};

export default Typography;
export { textStyles, fontWeights };
